import consumer from "../../shared/utils/cable_consumer"

export function RealtimeChatModal() {
  return {
    isOpen: false,
    isLoading: false,
    isAuthenticated: false,
    currentView: 'conversations',
    currentBrandId: null,
    currentBrandName: '',
    currentConversationId: null,
    brands: [],
    conversations: [],
    messages: [],
    messageInput: '',
    isConnected: false,
    isComposing: false,
    openMode: 'normal',
    chatSubscription: null,
    onlineUsers: new Set(),
    unreadByBrand: [],
    cleanupHandler: null,

    async init() {
      this.isAuthenticated = await this.checkAuthentication();

      window.toggleChatModal = () => {
        this.toggleChat();
      };

      window.openBrandChat = (brandId, brandName) => {
        this.openBrandChat(brandId, brandName);
      };

      this.bindGlobalEvents();
      this.bindUnreadCountEvents();
      this.setupCleanupListeners();
    },

    async checkAuthentication() {
      try {
        const response = await fetch('/chat/auth_status', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-CSRF-Token': this.getCSRFToken()
          }
        });

        if (response.ok) {
          const data = await response.json();

          this.authData = data;
          return !!data.authenticated;
        }

        this.authData = null;
        return false;
      } catch (error) {
        const csrfToken = document.querySelector('meta[name="csrf-token"]');
        this.authData = null;
        return !!csrfToken;
      }
    },

    bindGlobalEvents() {
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-action="open-brand-chat"]')) {
          const button = e.target.closest('[data-action="open-brand-chat"]');
          const brandId = button.dataset.brandId;
          const brandName = button.dataset.brandName;
          this.openBrandChat(parseInt(brandId), brandName);
        }
      });
    },

    bindUnreadCountEvents() {
      // 監聽未讀數量更新事件
      window.addEventListener('unread-count-updated', (event) => {
        this.unreadByBrand = event.detail.unreadByBrand || [];
      });
    },

    setupCleanupListeners() {
      if (this.cleanupHandler) return;

      this.cleanupHandler = () => this.destroy();
      document.addEventListener('turbo:before-cache', this.cleanupHandler);
      document.addEventListener('turbo:before-render', this.cleanupHandler);

      this.$cleanup = this.cleanupHandler;
    },

    getBrandUnreadCount(brandId) {
      const brandUnread = this.unreadByBrand.find(item => item.brand_id === brandId);
      return brandUnread ? brandUnread.unread_count : 0;
    },

    toggleChat() {
      if (this.isOpen) {
        this.closeChat();
      } else {
        this.openChat();
      }
    },

    async openChat() {
      const currentlyAuthenticated = await this.checkAuthentication();
      if (!currentlyAuthenticated) {
        this.currentView = 'login';
      } else {
        this.currentView = 'conversations';
        this.openMode = 'normal';
        this.loadConversations().catch(() => { });
      }
      this.isOpen = true;

      // 觸發聊天室開啟事件
      window.dispatchEvent(new CustomEvent('chat-opened'));
    },

    closeChat() {
      this.disconnectFromChat();
      this.isOpen = false;
      this.resetConnectionState();
    },

    async openBrandChat(brandId, brandName) {

      const currentlyAuthenticated = await this.checkAuthentication();
      if (!currentlyAuthenticated) {
        window.location.href = '/session/new';
        return;
      }

      this.openMode = 'brand_specific';
      this.currentBrandId = parseInt(brandId);
      this.currentBrandName = brandName;
      this.isOpen = true;
      await this.loadBrandConversation(brandId);
    },

    resetConnectionState() {
      this.currentBrandId = null;
      this.currentBrandName = '';
      this.currentConversationId = null;
      this.messages = [];
      this.messageInput = '';
      this.onlineUsers.clear();
    },

    async loadConversations() {
      try {
        const response = await fetch('/chat/conversations', {
          credentials: 'include',
          headers: {
            'X-CSRF-Token': this.getCSRFToken()
          }
        });
        const data = await response.json();
        this.conversations = data.conversations || [];
      } catch (error) {
        this.conversations = [];
      }
    },

    async loadBrands(onlyChatted = false) {
      try {
        const url = onlyChatted ? '/chat/brands?only_chatted=true' : '/chat/brands';
        const response = await fetch(url, {
          credentials: 'include',
          headers: {
            'X-CSRF-Token': this.getCSRFToken()
          }
        });
        const data = await response.json();
        this.brands = data.brands || [];
      } catch (error) {
        this.brands = [];
      }
    },

    selectConversation(conversation) {
      this.currentBrandId = conversation.brand.id;
      this.currentBrandName = conversation.brand.name;
      this.currentConversationId = conversation.id;
      this.loadConversationMessages(conversation);
    },

    selectBrand(brand) {
      this.currentBrandId = brand.id;
      this.currentBrandName = brand.name;
      this.loadBrandConversation(brand.id);
    },

    async loadConversationMessages(conversation) {
      this.isLoading = true;
      this.currentView = 'chat';

      try {
        let url = `/chat/messages?brand_id=${conversation.brand.id}`;

        // 如果是退換貨對話，加入退換貨 ID
        if (conversation.conversation_type === 'return_exchange' && conversation.context?.return_exchange_id) {
          url += `&return_exchange_id=${conversation.context.return_exchange_id}`;
        }

        const response = await fetch(url, {
          credentials: 'include',
          headers: {
            'X-CSRF-Token': this.getCSRFToken()
          }
        });

        if (response.status === 401) {
          this.currentView = 'login';
          return;
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          this.currentView = 'login';
          return;
        }

        const data = await response.json();
        this.messages = data.messages || [];
        this.currentConversationId = data.conversation_id;

        this.connectToChat();

        setTimeout(() => {
          this.scrollToBottom();
        }, 100);
      } catch (error) {
        if (error.message.includes('Unexpected token')) {
          this.currentView = 'login';
        } else {
          this.currentView = 'conversations';
        }
      } finally {
        this.isLoading = false;
      }
    },

    async loadBrandConversation(brandId) {
      this.isLoading = true;
      this.currentView = 'chat';

      try {
        const response = await fetch(`/chat/messages?brand_id=${brandId}`, {
          credentials: 'include',
          headers: {
            'X-CSRF-Token': this.getCSRFToken()
          }
        });

        if (response.status === 401) {
          this.currentView = 'login';
          return;
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          this.currentView = 'login';
          return;
        }

        const data = await response.json();
        this.messages = data.messages || [];
        this.currentConversationId = data.conversation_id;

        this.connectToChat();

        setTimeout(() => {
          this.scrollToBottom();
        }, 100);
      } catch (error) {
        if (error.message.includes('Unexpected token')) {
          this.currentView = 'login';
        } else {
          this.currentView = 'conversations';
        }
      } finally {
        this.isLoading = false;
      }
    },

    connectToChat() {
      if (!this.currentConversationId || this.chatSubscription) {
        return;
      }


      this.chatSubscription = consumer.subscriptions.create(
        {
          channel: "ChatChannel",
          conversation_id: this.currentConversationId
        },
        {
          connected: () => {
            this.isConnected = true;
            this.markMessagesAsRead();
          },

          disconnected: () => {
            this.isConnected = false;
          },

          received: (data) => {
            this.handleWebSocketMessage(data);
          }
        }
      );
    },

    disconnectFromChat() {
      if (this.chatSubscription) {
        this.chatSubscription.unsubscribe();
        this.chatSubscription = null;
        this.isConnected = false;
      }
    },

    handleWebSocketMessage(data) {
      switch (data.type) {
        case 'new_message':
          this.handleNewMessage(data.message);
          break;
        case 'messages_read':
          this.handleMessagesRead(data);
          break;
        case 'presence_update':
          this.handlePresenceUpdate(data);
          break;
        case 'unread_count_update':
          this.handleUnreadCountUpdate(data);
          break;
        case 'error':
          break;
      }
    },

    handleNewMessage(messageData) {
      const currentUserId = this.getCurrentUserId();
      messageData.is_own = messageData.user_id === currentUserId;

      const existingMessage = this.messages.find(msg => msg.id === messageData.id);
      if (existingMessage) {
        return;
      }

      if (messageData.is_own) {
        // 嘗試找到並替換臨時訊息
        for (let i = this.messages.length - 1; i >= 0; i--) {
          const msg = this.messages[i];
          if (msg.temp === true && msg.content === messageData.content && msg.user_id === currentUserId) {
            this.messages[i] = messageData;
            setTimeout(() => {
              this.scrollToBottom();
            }, 10);
            return;
          }
        }

        // 如果找不到臨時訊息，檢查是否已經有相同的真實訊息
        const realMessageExists = this.messages.some(msg =>
          !msg.temp &&
          msg.content === messageData.content &&
          msg.user_id === currentUserId &&
          Math.abs(new Date(msg.created_at) - new Date(messageData.created_at)) < 10000 // 10秒內
        );

        if (realMessageExists) {
          console.log('相同的真實訊息已存在，忽略廣播:', messageData);
          return;
        }

        // 如果找不到臨時訊息，且沒有相同真實訊息，說明可能有問題，但仍然忽略
        // 因為用戶不應該看到自己發送的訊息兩次
        console.log('找不到對應的臨時訊息，忽略自己的廣播:', messageData);
        return;
      }

      // 新增新訊息（只有別人的訊息）
      this.messages.push(messageData);
      setTimeout(() => {
        this.scrollToBottom();
      }, 10);

      // 如果不是自己的訊息，標記為已讀
      this.markMessagesAsRead();
    },

    handleMessagesRead(data) {
      // 更新訊息的已讀狀態
      data.message_ids.forEach(messageId => {
        const message = this.messages.find(msg => msg.id === messageId);
        if (message) {
          message.read = true;
        }
      });
    },

    handlePresenceUpdate(data) {
      if (data.status === 'online') {
        this.onlineUsers.add(data.user_id);
      } else {
        this.onlineUsers.delete(data.user_id);
      }
    },

    handleUnreadCountUpdate(data) {
      // 計算當前使用者的總未讀數量
      const currentUserId = this.getCurrentUserId();
      let totalUnreadCount = 0;
      let unreadByBrand = [];

      if (data.unread_counts && currentUserId) {
        // 如果是一般使用者，直接使用 conversation.user_id 的計數
        if (data.unread_counts[currentUserId] !== undefined) {
          totalUnreadCount = data.unread_counts[currentUserId];
        }

        // 如果是品牌使用者，從 brand_users 中獲取
        if (data.unread_counts.brand_users && data.unread_counts.brand_users[currentUserId]) {
          totalUnreadCount = data.unread_counts.brand_users[currentUserId];
        }
      }

      // 觸發即時未讀計數更新事件
      if (window.unreadBadgeInstance) {
        window.dispatchEvent(new CustomEvent('unread-count-realtime-update', {
          detail: {
            totalCount: totalUnreadCount,
            unreadByBrand: unreadByBrand,
            conversationId: data.conversation_id
          }
        }));
      }

      // 也觸發原有的事件，保持向後兼容
      window.dispatchEvent(new CustomEvent('messages-read'));
    },

    sendMessage() {
      const content = this.messageInput.trim();
      if (!content || !this.currentConversationId || !this.isConnected) {
        return;
      }


      // 更新 - 立即顯示訊息
      const tempMessage = {
        id: 'temp_' + Date.now(),
        content: content,
        user_id: this.getCurrentUserId(),
        is_own: true,
        created_at: new Date().toISOString(),
        read: false,
        temp: true
      };

      this.messages.push(tempMessage);
      this.messageInput = '';

      setTimeout(() => {
        this.scrollToBottom();
      }, 10);

      // 透過 WebSocket 發送訊息
      this.chatSubscription.perform('send_message', {
        content: content,
        message_type: 'text'
      });

      // 設置臨時訊息的超時清理機制（防止臨時訊息永遠不被替換）
      setTimeout(() => {
        this.cleanupTempMessage(tempMessage);
      }, 10000); // 10秒後清理
    },

    cleanupTempMessage(tempMessage) {
      // 檢查臨時訊息是否仍然存在
      const tempIndex = this.messages.findIndex(msg =>
        msg.id === tempMessage.id && msg.temp === true
      );

      if (tempIndex !== -1) {
        console.log('清理超時的臨時訊息:', tempMessage);
        this.messages.splice(tempIndex, 1);
      }
    },

    markMessagesAsRead() {
      if (this.chatSubscription && this.isConnected) {
        this.chatSubscription.perform('mark_as_read', {});

        // 觸發訊息已讀事件
        window.dispatchEvent(new CustomEvent('messages-read'));
      }
    },

    handleKeydown(event) {
      if (event.key === "Enter" && !event.shiftKey && !this.isComposing) {
        event.preventDefault()
        this.sendMessage()
      }
    },

    handleCompositionStart() {
      this.isComposing = true
    },

    handleCompositionEnd() {
      this.isComposing = false
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('zh-TW', {
        hour: '2-digit',
        minute: '2-digit',
        month: 'short',
        day: 'numeric'
      });
    },

    getCSRFToken() {
      const token = document.querySelector('meta[name="csrf-token"]');
      return token ? token.getAttribute('content') : '';
    },

    getCurrentUserId() {
      // 首先嘗試從 meta tag 獲取
      const userMeta = document.querySelector('meta[name="current-user-id"]');
      if (userMeta) {
        const userId = parseInt(userMeta.getAttribute('content'));
        return userId;
      }

      // 如果 meta tag 不存在，從之前的認證檢查獲取
      if (this.authData && this.authData.user_id) {
        return this.authData.user_id;
      }

      return null;
    },

    backToConversations() {
      this.disconnectFromChat();
      this.currentView = 'conversations';
      this.currentBrandId = null;
      this.currentBrandName = '';
      this.currentConversationId = null;
      this.messages = [];
      this.messageInput = '';
      this.onlineUsers.clear();

      // 重新載入對話列表
      this.loadConversations().catch(() => { });
    },

    // 輔助方法：檢查使用者是否在線
    isUserOnline(userId) {
      return this.onlineUsers.has(userId);
    },

    // 取得對話類型的顯示名稱
    getConversationTypeDisplay(conversation) {
      if (conversation.conversation_type === 'return_exchange') {
        return '退換貨討論';
      }
      return '一般客服';
    },

    // 取得對話類型的圖標
    getConversationTypeIcon(conversation) {
      if (conversation.conversation_type === 'return_exchange') {
        return '↩️';
      }
      return '💬';
    },

    // 取得對話的上下文資訊
    getConversationContext(conversation) {
      if (conversation.conversation_type === 'return_exchange' && conversation.context) {
        return `申請編號: ${conversation.context.application_number}`;
      }
      return '';
    },

    destroy() {
      this.disconnectFromChat();

      if (this.cleanupHandler) {
        document.removeEventListener('turbo:before-cache', this.cleanupHandler);
        document.removeEventListener('turbo:before-render', this.cleanupHandler);
        this.cleanupHandler = null;
      }
    }
  };
}
